import { useEffect, useState } from "react";
import { toFa } from "@soheil/core";
import { openExternal, rawInitData, haptic } from "../platform/telegram";
import { useToast } from "./Toast";

/**
 * ستارهٔ بی‌نهایت — صفحهٔ اشتراک.
 *
 * دو تصمیم که عمدی‌اند:
 *   ۱. وضعیت اشتراک از سرور خوانده می‌شود، نه از حالت محلی. اگر اپ خودش
 *      تصمیم بگیرد چه کسی پلاس است، نسخهٔ دست‌کاری‌شده ظرف یک هفته پخش می‌شود.
 *   ۲. لینک پرداخت در مرورگر باز می‌شود نه داخل مینی‌اپ — درگاه‌های ایرانی
 *      داخل وب‌ویو تلگرام کار نمی‌کنند.
 */

interface Plan {
  id: string;
  title: string;
  price: number;
  note: string | null;
  badge: string | null;
  available: boolean;
}

interface PlansOut {
  plans: Plan[];
  entitlement: { plus: boolean; plan: string; until: number | null };
  gateway: { configured: boolean; mode: string };
}

const money = (t: number) => `${toFa(t.toLocaleString("en-US"))} تومان`;

/**
 * فقط چیزهایی که همین امروز کار می‌کنند.
 * تبلیغ قابلیتی که هنوز ساخته نشده، سریع‌ترین راه از دست دادن اعتماد است —
 * و در ایران، جایی که کاربر یک بار پول می‌دهد، برگشتنی هم ندارد.
 */
const BENEFITS = [
  ["عادت و کار نامحدود", "نسخهٔ رایگان تا ۵ عادت و ۵۰ کار فعال"],
  ["سه «شب ابری» در ماه", "رایگان: یکی — یک روزِ از دست رفته را می‌بخشد"],
  ["حالت تعطیلات", "زنجیره‌ات در سفر نمی‌شکند"],
  ["خروجی گرفتن از داده‌ها", "همه‌چیز در یک فایل، مال خودت"],
];

export function Upgrade() {
  const [data, setData] = useState<PlansOut | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await fetch(`${location.origin}/api/plans`, {
          headers: { "X-Telegram-Init-Data": rawInitData() },
        });
        if (!r.ok) return;
        const out = (await r.json()) as PlansOut;
        if (alive) setData(out);
      } catch {
        // آفلاین — این صفحه اختیاری است، سکوت درست‌ترین کار است
      }
    })();
    return () => { alive = false; };
  }, []);

  // بیرون از تلگرام یا وقتی سرور در دسترس نیست، چیزی نشان نمی‌دهیم
  if (!data) return null;

  const ent = data.entitlement;

  if (ent.plus) {
    const until = ent.until
      ? new Date(ent.until).toLocaleDateString("fa-IR")
      : "همیشه";
    return (
      <section className="block">
        <div className="plus-active">
          <b>ستارهٔ بی‌نهایت فعال است ⭐</b>
          <span>{ent.until ? `تا ${until}` : "بدون تاریخ انقضا"}</span>
        </div>
      </section>
    );
  }

  const buy = async (planId: string) => {
    if (busy) return;
    setBusy(planId);
    haptic("tap");
    try {
      const r = await fetch(`${location.origin}/api/pay/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Telegram-Init-Data": rawInitData() },
        body: JSON.stringify({ plan: planId }),
      });
      const out = await r.json();
      if (r.ok && out.url) {
        openExternal(out.url);
        toast("صفحهٔ پرداخت در مرورگر باز شد");
      } else if (out.error === "gateway_not_configured") {
        toast("درگاه پرداخت هنوز فعال نشده");
      } else if (out.error === "lifetime_sold_out") {
        toast("ظرفیت مادام‌العمر تمام شد");
      } else {
        toast("ساختن لینک پرداخت نشد — کمی بعد دوباره امتحان کن");
      }
    } catch {
      toast("اینترنت وصل نیست");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="block">
      <h2 className="sect">ستارهٔ بی‌نهایت</h2>

      <ul className="benefits">
        {BENEFITS.map(([title, sub]) => (
          <li key={title}>
            <span className="benefit-mark">⭐</span>
            <div><b>{title}</b><span>{sub}</span></div>
          </li>
        ))}
      </ul>

      <div className="plans">
        {data.plans.map((pl) => (
          <button
            key={pl.id}
            className={`plan${pl.badge ? " plan-best" : ""}`}
            disabled={!pl.available || busy !== null}
            onClick={() => void buy(pl.id)}
          >
            {pl.badge && <em className="plan-badge">{pl.badge}</em>}
            <b>{pl.title}</b>
            <span className="plan-price">{money(pl.price)}</span>
            {pl.note && <span className="plan-note">{pl.available ? pl.note : "ظرفیت تمام شد"}</span>}
          </button>
        ))}
      </div>

      <p className="hint">
        پرداخت در مرورگر و از راه زرین‌پال انجام می‌شود. نسخهٔ رایگان سر جایش
        می‌ماند؛ این برای وقتی است که به سقفش خورده‌ای.
      </p>
    </section>
  );
}
